// Günlük tablo seçili tarihle, takvim görünümü 7 günlük aralıkla çalışır.
// Planlar yalnızca aynı günün üretim kayıtlarıyla eşleştirilir.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  Form,
  Spinner,
  Table,
} from "react-bootstrap";

import Layout from "../../layout/Layout";

import {
  uploadUretimProgramExcel,
  getUretimProgramCalendar,
  deactivateUretimProgramByDate,
} from "../../../api/planlama/PlanlamaCalendarapi";

import { boruProfilDaily } from "../../../api/uretim/boruProfilDaily";

const MACHINE_COUNT = 4;
const DAILY_CAPACITY_MINUTES = 950;
const AUTO_REFRESH_MILLISECONDS = 60_000;

const OFFICIAL_HOLIDAYS = [
  { date: "2026-01-01", name: "Yılbaşı" },
  { date: "2026-04-23", name: "23 Nisan" },
  { date: "2026-05-01", name: "1 Mayıs" },
  { date: "2026-05-19", name: "19 Mayıs" },
  { date: "2026-05-26", name: "Kurban Bayramı Arifesi" },
  { date: "2026-05-27", name: "Kurban Bayramı 1. Gün" },
  { date: "2026-05-28", name: "Kurban Bayramı 2. Gün" },
  { date: "2026-05-29", name: "Kurban Bayramı 3. Gün" },
  { date: "2026-05-30", name: "Kurban Bayramı 4. Gün" },
  { date: "2026-07-15", name: "15 Temmuz" },
  { date: "2026-08-30", name: "30 Ağustos" },
  { date: "2026-10-29", name: "29 Ekim" },
  { date: "2026-11-10", name: "10 Kasım" },
];

const getLocalISODate = (dateValue = new Date()) => {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";

  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().split("T")[0];
};

const createSafeDate = (isoDate) => new Date(`${isoDate}T12:00:00`);

const addDays = (isoDate, days) => {
  const date = createSafeDate(isoDate);
  date.setDate(date.getDate() + days);
  return getLocalISODate(date);
};

const normalizeApiDate = (value) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  return getLocalISODate(value);
};

const formatDateTR = (value) => {
  const normalized = normalizeApiDate(value);
  return normalized
    ? createSafeDate(normalized).toLocaleDateString("tr-TR")
    : "-";
};

const formatNumber = (value, maximumFractionDigits = 1) => {
  const number = Number(value || 0);
  if (!Number.isFinite(number)) return "0";

  return number.toLocaleString("tr-TR", {
    minimumFractionDigits: 0,
    maximumFractionDigits,
  });
};

const getApiErrorMessage = (error, fallbackMessage) => {
  const errors = error?.response?.data?.errors;

  if (Array.isArray(errors) && errors.length > 0) {
    return errors
      .map((item) => item?.message)
      .filter(Boolean)
      .join(" ");
  }

  return error?.response?.data?.message || error?.message || fallbackMessage;
};

const getArrayFromResponse = (response) => {
  const payload = response?.data ?? response;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const normalizeComparableText = (value) =>
  String(value ?? "")
    .trim()
    .toLocaleUpperCase("tr-TR")
    .replace(/İ/g, "I")
    .replace(/Ğ/g, "G")
    .replace(/Ü/g, "U")
    .replace(/Ş/g, "S")
    .replace(/Ö/g, "O")
    .replace(/Ç/g, "C")
    .replace(/[^A-Z0-9]/g, "");

const normalizeNumericText = (value) => {
  const number = Number(
    String(value ?? "")
      .trim()
      .replace(",", ".")
  );
  return Number.isFinite(number) ? Number(number.toFixed(3)).toString() : "";
};

const normalizePlanEbat = (value) => {
  const text = String(value ?? "")
    .trim()
    .toLocaleUpperCase("tr-TR")
    .replace(/,/g, ".")
    .replace(/[×*]/g, "X")
    .replace(/\s/g, "")
    .replace(/[ØΦQ]/g, "");

  const numbers = text.match(/\d+(?:\.\d+)?/g) || [];

  if (numbers.length >= 2) {
    return numbers
      .slice(0, 2)
      .map(Number)
      .sort((a, b) => b - a)
      .map((number) => Number(number.toFixed(3)).toString())
      .join("X");
  }

  if (numbers.length === 1) {
    return `D${normalizeNumericText(numbers[0])}`;
  }

  return normalizeComparableText(text);
};

const normalizeProductionEbat = (row) => {
  const en1 = Number(row?.en1 || 0);
  const en2 = Number(row?.en2 || 0);

  if (en1 > 0 && en2 > 0) {
    return [en1, en2]
      .sort((a, b) => b - a)
      .map((number) => Number(number.toFixed(3)).toString())
      .join("X");
  }

  return en1 > 0 ? `D${Number(en1.toFixed(3)).toString()}` : "";
};

const numbersClose = (a, b, tolerance) => {
  const first = Number(a || 0);
  const second = Number(b || 0);

  if (!first && !second) return true;
  if (!first || !second) return false;

  return Math.abs(first - second) <= tolerance;
};

const getPlanTargetKg = (plan) => {
  const targetKg = Number(plan?.hedefKg || 0);
  return targetKg > 0 ? targetKg : Number(plan?.siparisMiktariTon || 0) * 1000;
};

const getPlanPackageCount = (plan) =>
  Number(plan?.paketAdeti ?? plan?.miktarPaket ?? 0);

const getProductionPackageEquivalent = (row) => {
  const fullPackages = Number(row?.bagAdeti || 0);
  const halfPieces = Number(row?.yarimBag || 0);
  const piecesPerPackage = Number(row?.bagIci || 0);

  return (
    fullPackages + (piecesPerPackage > 0 ? halfPieces / piecesPerPackage : 0)
  );
};

const buildProductionGroups = (records) => {
  const map = new Map();

  records.forEach((row) => {
    if (row?.isActive === false) return;

    const productionDate = row?._productionDate || "";
    const machineNo = Number(row?.makinaAdi || 0);
    const firmaKey = normalizeComparableText(row?.firma);
    const ebatKey = normalizeProductionEbat(row);
    const kalinlik = Number(row?.kalinlik || 0);
    const boy = Number(row?.boy || 0);
    const bantEni = Number(row?.bantEni || 0);
    const paketIciAdet = Number(row?.bagIci || 0);

    const key = [
      productionDate,
      machineNo,
      firmaKey,
      ebatKey,
      kalinlik.toFixed(3),
      Math.round(boy),
      bantEni.toFixed(2),
      Math.round(paketIciAdet),
    ].join("|");

    if (!map.has(key)) {
      map.set(key, {
        key,
        productionDate,
        machineNo,
        firma: String(row?.firma || "").trim(),
        firmaKey,
        ebatKey,
        kalinlik,
        boy,
        bantEni,
        paketIciAdet,
        totalKg: 0,
        scrapKg: 0,
        fullPackages: 0,
        halfPieces: 0,
        packageEquivalent: 0,
        actualMinutes: 0,
        speedTotal: 0,
        speedCount: 0,
        records: [],
      });
    }

    const group = map.get(key);
    const fullPackages =
      Number(row?.bagAdeti || 0) + Number(row?.alternatif?.bagAdeti || 0);
    const halfPieces =
      Number(row?.yarimBag || 0) + Number(row?.alternatif?.yarimBag || 0);
    const actualKg =
      Number(row?.mamulMiktariKg || 0) + Number(row?.alternatif?.miktarKg || 0);
    const speed = Number(row?.gerceklesenHiz || 0);

    group.totalKg += actualKg;
    group.scrapKg += Number(row?.iskartaKg || 0);
    group.fullPackages += fullPackages;
    group.halfPieces += halfPieces;
    group.packageEquivalent += getProductionPackageEquivalent({
      ...row,
      bagAdeti: fullPackages,
      yarimBag: halfPieces,
    });
    group.actualMinutes += Number(row?.fiiliCalismaSuresi || 0);

    if (speed > 0) {
      group.speedTotal += speed;
      group.speedCount += 1;
    }

    group.records.push(row);
  });

  return Array.from(map.values()).map((group) => ({
    ...group,
    averageSpeed:
      group.speedCount > 0 ? group.speedTotal / group.speedCount : 0,
    remainingKg: group.totalKg,
    remainingPackageEquivalent: group.packageEquivalent,
  }));
};

const getMatchScore = (plan, group) => {
  const planDate = normalizeApiDate(plan?.planlananTarih);
  const isSameDay = group.productionDate === planDate;

  if (!isSameDay) return -1;
  if (Number(plan?.machineNo || 0) !== Number(group?.machineNo || 0)) return -1;

  const planFirma = normalizeComparableText(plan?.firma || plan?.musteri);
  const planEbat = normalizePlanEbat(plan?.ebat);

  if (planFirma && group.firmaKey && planFirma !== group.firmaKey) return -1;
  if (planEbat && group.ebatKey && planEbat !== group.ebatKey) return -1;
  if (!numbersClose(plan?.kalinlik, group?.kalinlik, 0.06)) return -1;
  if (!numbersClose(plan?.boy, group?.boy, 10)) return -1;

  if (
    Number(plan?.bantEni || 0) > 0 &&
    Number(group?.bantEni || 0) > 0 &&
    !numbersClose(plan?.bantEni, group?.bantEni, 1)
  ) {
    return -1;
  }

  if (
    Number(plan?.paketIciAdet || 0) > 0 &&
    Number(group?.paketIciAdet || 0) > 0 &&
    !numbersClose(plan?.paketIciAdet, group?.paketIciAdet, 1)
  ) {
    return -1;
  }

  let score = 20;
  if (planFirma && planFirma === group.firmaKey) score += 30;
  if (planEbat && planEbat === group.ebatKey) score += 30;
  if (numbersClose(plan?.kalinlik, group?.kalinlik, 0.01)) score += 15;
  if (numbersClose(plan?.boy, group?.boy, 1)) score += 15;
  if (numbersClose(plan?.bantEni, group?.bantEni, 0.1)) score += 5;
  if (numbersClose(plan?.paketIciAdet, group?.paketIciAdet, 0.1)) score += 5;

  return score;
};

const matchPlansWithProduction = (plans, productionRecords) => {
  const groups = buildProductionGroups(productionRecords);
  const resultMap = new Map();

  const sortedPlans = [...plans].sort((a, b) => {
    const dateCompare = String(a?.planlananTarih || "").localeCompare(
      String(b?.planlananTarih || "")
    );
    if (dateCompare !== 0) return dateCompare;

    const machineCompare =
      Number(a?.machineNo || 0) - Number(b?.machineNo || 0);
    if (machineCompare !== 0) return machineCompare;

    return Number(a?.siraNo || 999) - Number(b?.siraNo || 999);
  });

  sortedPlans.forEach((plan) => {
    const candidates = groups
      .map((group) => ({ group, score: getMatchScore(plan, group) }))
      .filter((candidate) => candidate.score >= 0)
      .sort((a, b) => {
        const aHasRemaining =
          a.group.remainingKg > 0 || a.group.remainingPackageEquivalent > 0
            ? 1
            : 0;
        const bHasRemaining =
          b.group.remainingKg > 0 || b.group.remainingPackageEquivalent > 0
            ? 1
            : 0;

        if (aHasRemaining !== bHasRemaining)
          return bHasRemaining - aHasRemaining;
        return b.score - a.score;
      });

    const selected = candidates[0];

    if (!selected) {
      resultMap.set(plan._id, { ...plan, actual: null });
      return;
    }

    const group = selected.group;
    const targetKg = getPlanTargetKg(plan);
    const targetPackages = getPlanPackageCount(plan);

    const allocatedKg =
      targetKg > 0 ? Math.min(group.remainingKg, targetKg) : group.remainingKg;

    const allocatedPackages =
      targetPackages > 0
        ? Math.min(group.remainingPackageEquivalent, targetPackages)
        : group.remainingPackageEquivalent;

    group.remainingKg = Math.max(group.remainingKg - allocatedKg, 0);
    group.remainingPackageEquivalent = Math.max(
      group.remainingPackageEquivalent - allocatedPackages,
      0
    );

    const hasProduction = allocatedKg > 0 || allocatedPackages > 0;
    const allCompleted =
      group.records.length > 0 &&
      group.records.every((record) => record?.isCompleted !== false);

    let status = plan?.status || "planned";

    if (hasProduction) {
      status = "produced";
    } else if (group.records.length > 0 && allCompleted) {
      status = "not_produced";
    } else if (group.records.length > 0) {
      status = "producing";
    }

    const operators = Array.from(
      new Set(
        group.records
          .map((record) => String(record?.operator || "").trim())
          .filter(Boolean)
      )
    );

    const shifts = Array.from(
      new Set(
        group.records
          .map((record) =>
            String(record?.vardiyaTipi || record?.vardiya || "").trim()
          )
          .filter(Boolean)
      )
    );

    resultMap.set(plan._id, {
      ...plan,
      backendStatus: plan?.status || "planned",
      status,
      actual: {
        matched: true,
        productionDate: group.productionDate,
        recordCount: group.records.length,
        allocatedKg,
        allocatedPackages,
        targetKg,
        completionRate: targetKg > 0 ? (allocatedKg / targetKg) * 100 : 0,
        averageSpeed: group.averageSpeed,
        scrapKg: group.scrapKg,
        actualMinutes: group.actualMinutes,
        operators,
        shifts,
      },
    });
  });

  return plans.map(
    (plan) => resultMap.get(plan._id) || { ...plan, actual: null }
  );
};

const getStatusConfig = (status) => {
  if (status === "produced") {
    return {
      label: "Üretildi",
      badge: "success",
      border: "#198754",
      background: "#edf8f1",
    };
  }

  if (status === "producing") {
    return {
      label: "Üretiliyor",
      badge: "warning",
      border: "#fd7e14",
      background: "#fff8e7",
    };
  }

  if (status === "partial") {
    return {
      label: "Kısmi Üretildi",
      badge: "warning",
      border: "#ffc107",
      background: "#fffbea",
    };
  }

  if (status === "not_produced") {
    return {
      label: "Üretilemedi",
      badge: "danger",
      border: "#dc3545",
      background: "#fff1f2",
    };
  }

  if (status === "postponed") {
    return {
      label: "Ertelendi",
      badge: "secondary",
      border: "#6c757d",
      background: "#f3f4f6",
    };
  }

  return {
    label: "Planlandı",
    badge: "primary",
    border: "#0d6efd",
    background: "#f8fbff",
  };
};

function UretimPlanlamaPage() {
  const [plannedJobs, setPlannedJobs] = useState([]);
  const [productionRecords, setProductionRecords] = useState([]);
  const [startDate, setStartDate] = useState(getLocalISODate());
  const [zoomLevel, setZoomLevel] = useState(1);
  const [viewMode, setViewMode] = useState("calendar");

  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [deactivateLoading, setDeactivateLoading] = useState(false);

  const [excelFileName, setExcelFileName] = useState("");
  const [importMessage, setImportMessage] = useState("");
  const [importError, setImportError] = useState("");
  const [productionWarning, setProductionWarning] = useState("");

  const dates = useMemo(
    () => Array.from({ length: 7 }, (_, index) => addDays(startDate, index)),
    [startDate]
  );

  const endDate = useMemo(() => addDays(startDate, 6), [startDate]);

  const fetchCalendar = useCallback(
    async (silent = false) => {
      if (!silent) setLoading(true);

      setImportError("");
      setProductionWarning("");

      try {
        // Takvim görünümü 7 günlük, düz tablo görünümü yalnızca seçili günlüktür.
        // Örn. tabloda 2026-07-25 seçildiyse API'ye 25-25 gönderilir.
        const requestEndDate = viewMode === "table" ? startDate : endDate;

        const calendarResponse = await getUretimProgramCalendar(
          startDate,
          requestEndDate
        );
        const apiPlans = Array.isArray(calendarResponse?.data?.data)
          ? calendarResponse.data.data
          : [];

        const normalizedPlans = apiPlans.map((job) => ({
          ...job,
          planlananTarih: normalizeApiDate(job.planlananTarih),
          programTarihi: normalizeApiDate(job.programTarihi),
          planTarihi: normalizeApiDate(job.planTarihi),
          terminTarihi: normalizeApiDate(job.terminTarihi),
          originalPlanlananTarih: normalizeApiDate(job.originalPlanlananTarih),
          rescheduledFrom: normalizeApiDate(job.rescheduledFrom),
        }));

        const productionDates = Array.from(
          new Set(
            normalizedPlans
              .map((plan) => plan?.planlananTarih || "")
              .filter(Boolean)
          )
        );

        const responses = await Promise.allSettled(
          productionDates.map(async (productionDate) => {
            const response = await boruProfilDaily(productionDate);
            return getArrayFromResponse(response).map((record) => ({
              ...record,
              _productionDate: productionDate,
            }));
          })
        );

        const failedDates = [];
        const actualRows = [];

        responses.forEach((result, index) => {
          if (result.status === "fulfilled") {
            actualRows.push(...result.value);
          } else {
            failedDates.push(productionDates[index]);
          }
        });

        setProductionRecords(actualRows);
        setPlannedJobs(matchPlansWithProduction(normalizedPlans, actualRows));

        if (failedDates.length > 0) {
          setProductionWarning(
            `${failedDates.join(", ")} tarihleri için üretim verisi alınamadı.`
          );
        }
      } catch (error) {
        console.error("Üretim programı yüklenirken hata:", error);
        setPlannedJobs([]);
        setProductionRecords([]);
        setImportError(
          getApiErrorMessage(
            error,
            "Üretim programı ve üretim verileri alınamadı."
          )
        );
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [startDate, endDate, viewMode]
  );

  useEffect(() => {
    fetchCalendar(false);

    const intervalId = setInterval(() => {
      fetchCalendar(true);
    }, AUTO_REFRESH_MILLISECONDS);

    return () => clearInterval(intervalId);
  }, [fetchCalendar]);

  const groupedJobs = useMemo(() => {
    const groups = {};

    plannedJobs.forEach((job) => {
      if (!job.planlananTarih || !job.machineNo) return;

      const key = `${Number(job.machineNo)}_${job.planlananTarih}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(job);
    });

    Object.values(groups).forEach((jobs) => {
      jobs.sort((a, b) => Number(a.siraNo || 999) - Number(b.siraNo || 999));
    });

    return groups;
  }, [plannedJobs]);

  const flatJobsByMachine = useMemo(() => {
    const groups = {};
    for (let machineNo = 1; machineNo <= MACHINE_COUNT; machineNo += 1) {
      groups[machineNo] = [];
    }

    plannedJobs
      .filter((job) => normalizeApiDate(job?.planlananTarih) === startDate)
      .forEach((job) => {
        const machineNo = Number(job?.machineNo || 0);
        if (!groups[machineNo]) groups[machineNo] = [];
        groups[machineNo].push(job);
      });

    Object.values(groups).forEach((jobs) => {
      jobs.sort((a, b) => {
        const dateCompare = String(a?.planlananTarih || "").localeCompare(
          String(b?.planlananTarih || "")
        );
        return dateCompare !== 0
          ? dateCompare
          : Number(a?.siraNo || 999) - Number(b?.siraNo || 999);
      });
    });

    return groups;
  }, [plannedJobs, startDate]);

  const summary = useMemo(
    () =>
      plannedJobs.reduce(
        (total, job) => {
          total.planCount += 1;
          total.targetKg += getPlanTargetKg(job);
          total.actualKg += Number(job?.actual?.allocatedKg || 0);

          if (job.status === "produced") total.producedCount += 1;
          else if (job.status === "not_produced") total.notProducedCount += 1;
          else total.plannedCount += 1;

          return total;
        },
        {
          planCount: 0,
          producedCount: 0,
          notProducedCount: 0,
          plannedCount: 0,
          targetKg: 0,
          actualKg: 0,
        }
      ),
    [plannedJobs]
  );

  const getCellSummary = (machineNo, date) => {
    const jobs = groupedJobs[`${machineNo}_${date}`] || [];

    return jobs.reduce(
      (total, job) => ({
        tonnage: total.tonnage + getPlanTargetKg(job) / 1000,
        actualTonnage:
          total.actualTonnage + Number(job?.actual?.allocatedKg || 0) / 1000,
        minutes: total.minutes + Number(job.totalMinutes || 0),
      }),
      { tonnage: 0, actualTonnage: 0, minutes: 0 }
    );
  };

  const getDailySummary = (date) =>
    plannedJobs
      .filter((job) => job.planlananTarih === date)
      .reduce(
        (total, job) => ({
          tonnage: total.tonnage + getPlanTargetKg(job) / 1000,
          actualTonnage:
            total.actualTonnage + Number(job?.actual?.allocatedKg || 0) / 1000,
          minutes: total.minutes + Number(job.totalMinutes || 0),
        }),
        { tonnage: 0, actualTonnage: 0, minutes: 0 }
      );

  const handleExcelSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadLoading(true);
    setImportError("");
    setImportMessage("");
    setExcelFileName(file.name);

    try {
      const response = await uploadUretimProgramExcel(file, "", true);
      const uploadedProgramDate = response?.data?.data?.programTarihi || "";
      const count =
        response?.data?.data?.rowCount ??
        response?.data?.data?.recordCount ??
        0;

      setImportMessage(
        response?.data?.message || `${count} üretim planı takvime eklendi.`
      );

      if (uploadedProgramDate && uploadedProgramDate !== startDate) {
        setStartDate(uploadedProgramDate);
      } else {
        await fetchCalendar(false);
      }
    } catch (error) {
      console.error("Excel yüklenirken hata:", error);
      setExcelFileName("");
      setImportError(
        getApiErrorMessage(error, "Excel üretim programı yüklenemedi.")
      );
    } finally {
      setUploadLoading(false);
      event.target.value = "";
    }
  };

  const handleDeactivateSelectedDate = async () => {
    if (
      !window.confirm(
        `${formatDateTR(startDate)} tarihli program pasife alınsın mı?`
      )
    ) {
      return;
    }

    setDeactivateLoading(true);
    setImportError("");

    try {
      const response = await deactivateUretimProgramByDate(startDate);
      setImportMessage(response?.data?.message || "Program pasife alındı.");
      await fetchCalendar(false);
    } catch (error) {
      setImportError(getApiErrorMessage(error, "Program pasife alınamadı."));
    } finally {
      setDeactivateLoading(false);
    }
  };

  const isBusy = loading || uploadLoading || deactivateLoading;

  const renderPlanCard = (job, index, machineNo, date) => {
    const statusConfig = getStatusConfig(job.status);
    const actual = job?.actual;
    const customerName = job.firma || job.musteri || "Firma belirtilmedi";

    return (
      <div
        key={job._id || `${machineNo}-${date}-${index}`}
        className="plan-item"
        style={{
          borderLeftColor: statusConfig.border,
          backgroundColor: statusConfig.background,
        }}
      >
        {Number(job.setupMinutes) > 0 && (
          <div className="small fw-bold text-warning-emphasis text-center mb-2">
            Kalıp değişimi: {formatNumber(job.setupMinutes, 0)} dk
          </div>
        )}

        <div className="d-flex justify-content-between align-items-start gap-2">
          <div style={{ minWidth: 0 }}>
            <div className="small text-muted fw-bold">
              #{job.siraNo || index + 1}
            </div>
            <div
              className="fw-bold text-dark text-truncate"
              title={customerName}
              style={{ fontSize: "0.82rem" }}
            >
              {customerName}
            </div>
          </div>

          <Badge bg={statusConfig.badge} className="flex-shrink-0">
            {formatNumber(getPlanTargetKg(job) / 1000, 2)} T
          </Badge>
        </div>

        <div className="small text-muted mt-2" style={{ lineHeight: 1.55 }}>
          <div>
            <b>{job.stokCinsi || job.kalite || "-"}</b>
          </div>
          <div>
            {job.ebat || "-"} | {formatNumber(job.kalinlik, 2)} mm | Boy:{" "}
            {formatNumber(job.boy, 0)}
          </div>
          <div>Bant eni: {formatNumber(job.bantEni, 2)} mm</div>
          <div>Plan paket: {formatNumber(getPlanPackageCount(job), 0)}</div>
        </div>

        {actual?.matched && (
          <div className="actual-production-box mt-2">
            <div className="d-flex justify-content-between gap-2">
              <b>Gerçekleşen</b>
              <b>{formatNumber(actual.allocatedKg / 1000, 2)} T</b>
            </div>
            <div>
              Paket: <b>{formatNumber(actual.allocatedPackages, 2)}</b>
            </div>
            <div>
              Hız: <b>{formatNumber(actual.averageSpeed, 1)}</b>
            </div>
            <div>
              Üretim günü: <b>{formatDateTR(actual.productionDate)}</b>
            </div>
            {actual.shifts?.length > 0 && (
              <div>Vardiya: {actual.shifts.join(", ")}</div>
            )}
            <div>
              Hedef karşılama: <b>%{formatNumber(actual.completionRate, 1)}</b>
            </div>
          </div>
        )}

        <div className="d-flex justify-content-between align-items-center border-top mt-2 pt-2">
          <span className="small fw-bold text-dark">
            <i className="bi bi-clock me-1" />
            {formatNumber(job.totalMinutes, 0)} dk
          </span>
          <Badge bg={statusConfig.badge}>{statusConfig.label}</Badge>
        </div>
      </div>
    );
  };

  return (
    <Layout>
      <style>{`
        .production-board .plan-cell { min-height: 180px; vertical-align: top; }
        .production-board .plan-list { display: flex; min-height: 164px; flex-direction: column; gap: 8px; }
        .production-board .plan-item { border: 1px solid #e9ecef; border-left-width: 4px; border-radius: 8px; padding: 9px; box-shadow: 0 2px 7px rgba(33,37,41,.07); }
        .production-board .actual-production-box { border: 1px solid #badbcc; border-radius: 6px; padding: 7px; background: rgba(209,231,221,.72); color: #0f5132; font-size: .68rem; line-height: 1.5; }
        .production-board .empty-cell { display: flex; min-height: 145px; align-items: center; justify-content: center; color: #adb5bd; font-size: .75rem; font-weight: 600; }
        .production-board .holiday-column { background: #fff6f6 !important; }
        .production-board .capacity-exceeded { background: #fff3cd !important; color: #842029; }
        .production-board .loading-overlay { position: absolute; inset: 0; z-index: 10; display: flex; align-items: center; justify-content: center; background: rgba(255,255,255,.72); backdrop-filter: blur(1px); }
        .production-board .summary-stat { min-width: 135px; border: 1px solid #e9ecef; border-radius: 8px; padding: 8px 12px; background: #fff; }
        .production-board .machine-section-row td { background: #343a40 !important; color: #fff !important; font-weight: 700; border-color: #495057 !important; }
        .production-board .flat-status-produced td { background: #f0fff4; }
        .production-board .flat-status-not-produced td { background: #fff5f5; }
      `}</style>

      <div className="production-board">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3 p-3 bg-white shadow-sm rounded border-start border-primary border-4">
          <div>
            <h4 className="fw-bold mb-1 text-dark">Üretim Planlama Panosu</h4>
            <div className="text-muted small">
              Excel planı ile günlük boru-profil üretim kayıtları otomatik
              eşleştirilir.
            </div>
          </div>

          <div className="d-flex flex-wrap gap-2">
            <input
              id="production-plan-excel"
              type="file"
              accept=".xlsx,.xlsm"
              hidden
              disabled={isBusy}
              onChange={handleExcelSelect}
            />

            <Button
              variant="success"
              className="fw-semibold"
              disabled={isBusy}
              onClick={() =>
                document.getElementById("production-plan-excel")?.click()
              }
            >
              {uploadLoading ? (
                <Spinner size="sm" className="me-2" />
              ) : (
                <i className="bi bi-file-earmark-excel me-2" />
              )}
              {uploadLoading ? "Excel Yükleniyor..." : "Excel Planını Yükle"}
            </Button>

            <Button
              variant="outline-danger"
              className="fw-semibold"
              disabled={isBusy}
              onClick={handleDeactivateSelectedDate}
            >
              {deactivateLoading ? (
                <Spinner size="sm" className="me-2" />
              ) : (
                <i className="bi bi-trash3 me-2" />
              )}
              Seçili Günü Temizle
            </Button>

            <Button
              variant="outline-primary"
              className="fw-semibold"
              disabled={isBusy}
              onClick={() => fetchCalendar(false)}
            >
              {loading ? (
                <Spinner size="sm" className="me-2" />
              ) : (
                <i className="bi bi-arrow-clockwise me-2" />
              )}
              Yenile
            </Button>
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <div className="summary-stat">
            <div className="small text-muted">Plan Kaydı</div>
            <div className="fw-bold fs-5">{summary.planCount}</div>
          </div>
          <div className="summary-stat">
            <div className="small text-muted">Üretildi</div>
            <div className="fw-bold fs-5 text-success">
              {summary.producedCount}
            </div>
          </div>
          <div className="summary-stat">
            <div className="small text-muted">Üretilemedi</div>
            <div className="fw-bold fs-5 text-danger">
              {summary.notProducedCount}
            </div>
          </div>
          <div className="summary-stat">
            <div className="small text-muted">Planlanan Tonaj</div>
            <div className="fw-bold fs-5">
              {formatNumber(summary.targetKg / 1000, 2)} T
            </div>
          </div>
          <div className="summary-stat">
            <div className="small text-muted">Eşleşen Üretim</div>
            <div className="fw-bold fs-5 text-success">
              {formatNumber(summary.actualKg / 1000, 2)} T
            </div>
          </div>
          <div className="summary-stat">
            <div className="small text-muted">Ham Üretim Kaydı</div>
            <div className="fw-bold fs-5 text-primary">
              {productionRecords.length}
            </div>
          </div>
        </div>

        {importMessage && (
          <Alert
            variant="success"
            dismissible
            onClose={() => setImportMessage("")}
            className="py-2 shadow-sm"
          >
            <i className="bi bi-check-circle-fill me-2" />
            {importMessage}
            {excelFileName && (
              <span className="ms-2 text-muted">({excelFileName})</span>
            )}
          </Alert>
        )}

        {importError && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => setImportError("")}
            className="py-2 shadow-sm"
          >
            <i className="bi bi-exclamation-triangle-fill me-2" />
            {importError}
          </Alert>
        )}

        {productionWarning && (
          <Alert
            variant="warning"
            dismissible
            onClose={() => setProductionWarning("")}
            className="py-2 shadow-sm"
          >
            <i className="bi bi-exclamation-circle-fill me-2" />
            {productionWarning}
          </Alert>
        )}

        <Card className="border-0 shadow-sm position-relative">
          {loading && (
            <div className="loading-overlay">
              <div className="text-center">
                <Spinner animation="border" variant="primary" />
                <div className="small fw-semibold text-primary mt-2">
                  Plan ve üretim verileri eşleştiriliyor...
                </div>
              </div>
            </div>
          )}

          <Card.Header className="bg-white py-3">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div className="d-flex flex-wrap align-items-center gap-2">
                <ButtonGroup>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={isBusy}
                    onClick={() => setStartDate(addDays(startDate, -7))}
                  >
                    Hafta Geri
                  </Button>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={isBusy}
                    onClick={() => setStartDate(addDays(startDate, -1))}
                  >
                    Gün Geri
                  </Button>
                </ButtonGroup>

                <div className="d-flex">
                  <Form.Control
                    type="date"
                    size="sm"
                    value={startDate}
                    disabled={isBusy}
                    onChange={(event) =>
                      event.target.value && setStartDate(event.target.value)
                    }
                    style={{
                      width: 145,
                      borderTopRightRadius: 0,
                      borderBottomRightRadius: 0,
                    }}
                  />
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={isBusy}
                    onClick={() => setStartDate(getLocalISODate())}
                    style={{
                      borderTopLeftRadius: 0,
                      borderBottomLeftRadius: 0,
                    }}
                  >
                    Bugün
                  </Button>
                </div>

                <ButtonGroup>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    disabled={isBusy}
                    onClick={() => setStartDate(addDays(startDate, 1))}
                  >
                    Gün İleri
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    disabled={isBusy}
                    onClick={() => setStartDate(addDays(startDate, 7))}
                  >
                    Hafta İleri
                  </Button>
                </ButtonGroup>
              </div>

              <div className="d-flex gap-2">
                <ButtonGroup>
                  <Button
                    variant={
                      viewMode === "calendar" ? "primary" : "outline-primary"
                    }
                    size="sm"
                    onClick={() => setViewMode("calendar")}
                  >
                    <i className="bi bi-calendar3 me-1" />
                    Takvim
                  </Button>
                  <Button
                    variant={
                      viewMode === "table" ? "primary" : "outline-primary"
                    }
                    size="sm"
                    onClick={() => setViewMode("table")}
                  >
                    <i className="bi bi-table me-1" />
                    Günlük Tablo
                  </Button>
                </ButtonGroup>

                {viewMode === "calendar" && (
                  <ButtonGroup>
                    <Button
                      variant="outline-dark"
                      size="sm"
                      onClick={() =>
                        setZoomLevel((value) => Math.max(value - 0.1, 0.5))
                      }
                    >
                      <i className="bi bi-zoom-out" />
                    </Button>
                    <Button
                      variant="outline-dark"
                      size="sm"
                      style={{ width: 62 }}
                      onClick={() => setZoomLevel(1)}
                    >
                      {Math.round(zoomLevel * 100)}%
                    </Button>
                    <Button
                      variant="outline-dark"
                      size="sm"
                      onClick={() =>
                        setZoomLevel((value) => Math.min(value + 0.1, 1.5))
                      }
                    >
                      <i className="bi bi-zoom-in" />
                    </Button>
                  </ButtonGroup>
                )}
              </div>
            </div>
          </Card.Header>

          {viewMode === "calendar" ? (
            <div className="table-responsive">
              <div style={{ zoom: zoomLevel, minWidth: 1250 }}>
                <Table
                  bordered
                  className="mb-0 align-middle"
                  style={{ tableLayout: "fixed" }}
                >
                  <thead className="text-center">
                    <tr className="table-dark">
                      <th style={{ width: 125 }}>Makineler</th>
                      {dates.map((date) => {
                        const holiday = OFFICIAL_HOLIDAYS.find(
                          (item) => item.date === date
                        );
                        const isHoliday =
                          createSafeDate(date).getDay() === 0 ||
                          Boolean(holiday);

                        return (
                          <th
                            key={date}
                            className={isHoliday ? "bg-danger text-white" : ""}
                          >
                            <div>
                              {createSafeDate(date).toLocaleDateString(
                                "tr-TR",
                                {
                                  weekday: "short",
                                  day: "2-digit",
                                  month: "short",
                                }
                              )}
                            </div>
                            {isHoliday && (
                              <div
                                className="mt-1"
                                style={{ fontSize: ".65rem" }}
                              >
                                {holiday?.name || "Pazar"}
                              </div>
                            )}
                          </th>
                        );
                      })}
                    </tr>
                  </thead>

                  <tbody>
                    {Array.from(
                      { length: MACHINE_COUNT },
                      (_, index) => index + 1
                    ).map((machineNo) => (
                      <React.Fragment key={machineNo}>
                        <tr>
                          <td className="fw-bold text-center text-secondary bg-light">
                            BORU {machineNo}
                          </td>
                          {dates.map((date) => {
                            const jobs =
                              groupedJobs[`${machineNo}_${date}`] || [];
                            const cellSummary = getCellSummary(machineNo, date);
                            const isOver =
                              cellSummary.minutes > DAILY_CAPACITY_MINUTES;
                            const isHoliday =
                              createSafeDate(date).getDay() === 0 ||
                              OFFICIAL_HOLIDAYS.some(
                                (item) => item.date === date
                              );

                            return (
                              <td
                                key={`${machineNo}-${date}`}
                                className={`plan-cell p-2 ${
                                  isHoliday ? "holiday-column" : ""
                                }`}
                              >
                                <div className="plan-list">
                                  {jobs.length === 0 ? (
                                    <div className="empty-cell">
                                      Plan bulunmuyor
                                    </div>
                                  ) : (
                                    jobs.map((job, index) =>
                                      renderPlanCard(
                                        job,
                                        index,
                                        machineNo,
                                        date
                                      )
                                    )
                                  )}
                                </div>
                                {isOver && (
                                  <div className="alert alert-warning py-1 px-2 mb-0 mt-2 text-center small fw-bold">
                                    Kapasite aşıldı:{" "}
                                    {formatNumber(cellSummary.minutes, 0)} dk
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>

                        <tr className="table-light border-bottom border-2">
                          <td className="text-end pe-2 fw-bold text-muted small">
                            BORU{machineNo} Toplam:
                          </td>
                          {dates.map((date) => {
                            const cellSummary = getCellSummary(machineNo, date);
                            return (
                              <td
                                key={`summary-${machineNo}-${date}`}
                                className="text-center small"
                              >
                                {cellSummary.tonnage > 0 ? (
                                  <>
                                    <div className="fw-bold">
                                      Plan:{" "}
                                      {formatNumber(cellSummary.tonnage, 2)} T
                                    </div>
                                    <div className="fw-bold text-success">
                                      Üretim:{" "}
                                      {formatNumber(
                                        cellSummary.actualTonnage,
                                        2
                                      )}{" "}
                                      T
                                    </div>
                                    <div className="text-primary fw-bold">
                                      {formatNumber(cellSummary.minutes, 0)} dk
                                    </div>
                                  </>
                                ) : (
                                  "-"
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      </React.Fragment>
                    ))}

                    <tr className="table-dark border-top border-4">
                      <td className="fw-bold text-center bg-success text-white">
                        GENEL TOPLAM
                      </td>
                      {dates.map((date) => {
                        const daily = getDailySummary(date);
                        return (
                          <td key={`daily-${date}`} className="text-center">
                            {daily.tonnage > 0 ? (
                              <>
                                <div
                                  className="fw-bold"
                                  style={{ color: "#ffc107" }}
                                >
                                  Plan: {formatNumber(daily.tonnage, 2)} T
                                </div>
                                <div className="fw-bold text-success">
                                  Üretim: {formatNumber(daily.actualTonnage, 2)}{" "}
                                  T
                                </div>
                                <div className="small text-white opacity-75">
                                  {formatNumber(daily.minutes, 0)} dk
                                </div>
                              </>
                            ) : (
                              "-"
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </Table>
              </div>
            </div>
          ) : (
            <div>
              <div className="d-flex justify-content-between align-items-center px-3 py-2 border-bottom bg-light">
                <div className="fw-bold text-dark">
                  {formatDateTR(startDate)} Günlük Üretim Planı
                </div>
                <Badge bg="primary">
                  {plannedJobs.filter(
                    (job) =>
                      normalizeApiDate(job?.planlananTarih) === startDate
                  ).length}{" "}
                  plan
                </Badge>
              </div>

              <div className="table-responsive">
                <Table bordered hover className="mb-0 align-middle small">
                  <thead className="table-light text-center">
                    <tr>
                      <th>Tarih</th>
                      <th>Sıra</th>
                      <th>Firma</th>
                      <th>Stok</th>
                      <th>Ebat</th>
                      <th>Kalınlık</th>
                      <th>Boy</th>
                      <th>Bant Eni</th>
                      <th>Plan Paket</th>
                      <th>Plan Kg</th>
                      <th>Gerçek Paket</th>
                      <th>Gerçek Kg</th>
                      <th>Hız</th>
                      <th>Vardiya</th>
                      <th>Üretim Günü</th>
                      <th>Durum</th>
                    </tr>
                  </thead>

                <tbody>
                  {Array.from(
                    { length: MACHINE_COUNT },
                    (_, index) => index + 1
                  ).map((machineNo) => {
                    const jobs = flatJobsByMachine[machineNo] || [];

                    return (
                      <React.Fragment key={`machine-${machineNo}`}>
                        <tr className="machine-section-row">
                          <td colSpan={16}>
                            BORU {machineNo}{" "}
                            <span className="ms-3 fw-normal opacity-75">
                              {jobs.length} plan
                            </span>
                          </td>
                        </tr>

                        {jobs.length === 0 ? (
                          <tr>
                            <td
                              colSpan={16}
                              className="text-center text-muted py-3"
                            >
                              Plan bulunmuyor.
                            </td>
                          </tr>
                        ) : (
                          jobs.map((job, index) => {
                            const actual = job?.actual;
                            const statusConfig = getStatusConfig(job.status);
                            const rowClass =
                              job.status === "produced"
                                ? "flat-status-produced"
                                : job.status === "not_produced"
                                  ? "flat-status-not-produced"
                                  : "";

                            return (
                              <tr
                                key={job._id || `${machineNo}-${index}`}
                                className={rowClass}
                              >
                                <td className="text-center fw-semibold">
                                  {formatDateTR(job.planlananTarih)}
                                </td>
                                <td className="text-center">
                                  {job.siraNo || index + 1}
                                </td>
                                <td className="fw-bold">
                                  {job.firma || job.musteri || "-"}
                                </td>
                                <td>{job.stokCinsi || job.kalite || "-"}</td>
                                <td className="text-center fw-semibold">
                                  {job.ebat || "-"}
                                </td>
                                <td className="text-end">
                                  {formatNumber(job.kalinlik, 2)}
                                </td>
                                <td className="text-end">
                                  {formatNumber(job.boy, 0)}
                                </td>
                                <td className="text-end">
                                  {formatNumber(job.bantEni, 2)}
                                </td>
                                <td className="text-end">
                                  {formatNumber(getPlanPackageCount(job), 0)}
                                </td>
                                <td className="text-end fw-bold">
                                  {formatNumber(getPlanTargetKg(job), 0)}
                                </td>
                                <td className="text-end text-success fw-bold">
                                  {actual
                                    ? formatNumber(actual.allocatedPackages, 2)
                                    : "-"}
                                </td>
                                <td className="text-end text-success fw-bold">
                                  {actual
                                    ? formatNumber(actual.allocatedKg, 0)
                                    : "-"}
                                </td>
                                <td className="text-end">
                                  {actual
                                    ? formatNumber(actual.averageSpeed, 1)
                                    : "-"}
                                </td>
                                <td className="text-center">
                                  {actual?.shifts?.length
                                    ? actual.shifts.join(", ")
                                    : "-"}
                                </td>
                                <td className="text-center">
                                  {actual?.productionDate
                                    ? formatDateTR(actual.productionDate)
                                    : "-"}
                                </td>
                                <td className="text-center">
                                  <Badge bg={statusConfig.badge}>
                                    {statusConfig.label}
                                  </Badge>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
                </Table>
              </div>
            </div>
          )}
        </Card>

        <div className="small text-muted mt-3">
          Eşleştirme: yalnızca aynı gün + makine + firma + ebat + kalınlık +
          boy + bant eni + paket içi adet. Eşleşen kayıtta üretim miktarı varsa
          “Üretildi” gösterilir. Veriler 60 saniyede bir yenilenir.
        </div>
      </div>
    </Layout>
  );
}

export default UretimPlanlamaPage;
